import { Injectable } from '@angular/core';
import { NutritionService } from '@app/core/services';
import { PanelSectionTypeEnum, ReproductionStatusCode, ProductType, Helper, Programs } from '@app/shared/utils';
import { AppState } from '@app/store';
import { selectConsultation } from '@app/store/consultation/consultation.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  getAllTreatsRecommendationQuantitySuccess,
  getAllWetRecommendationQuantitySuccess,
  getAllowanceRecommendation,
  getAllowanceRecommendationAllTreatsQuantity,
  getAllowanceRecommendationAllWetQuantity,
  getAllowanceRecommendationFail,
  getAllowanceRecommendationSuccess,
  setAllowanceCurrentBlockEditing,
  setAllowanceDefaultRecommendation,
  setAllowanceLoadingRecommendation,
  setAllowanceRecommendationForGestation,
} from '../actions/allowance-recommendation.actions';
import { selectAllowanceCurrentProgram } from '../selectors/allowance-program.selectors';
import { selectAllowance, selectCurrentRecommendation, selectTreatsQuantityPerDay } from '../selectors/allowance.selectors';
import { buildRationingApiBody } from '@app/shared/utils/static-helpers/rationing-helper';
import { Router } from '@angular/router';
import { translateKey } from '@app/shared/utils/static-helpers/translate';
import { selectSelectedProducts, selectSelectedPackIds, selectSelectedWetProduct } from '@app/store/products/products.selectors';
import { AllowanceCalculatorBlocks } from '@app/shared/utils/enums/allowance-calculator-blocks.enum';
import { selectCurrentBigMeasurementUnit, selectCurrentClinicCountry } from '@app/store/vet';
import { selectNavigationRoutes } from '../selectors/allowance-params.selectors';
import { CoreFacade } from '@app/store/core/core.facade';
import { RcToastTypes } from '@app/core/models/toast';

@Injectable()
export class AllowanceRecommendationEffects {
  /**
   * Get recommendation
   */
  getAllowanceRecommendation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAllowanceRecommendation),
      tap(() => this.store$.dispatch(setAllowanceLoadingRecommendation({ value: true }))),
      withLatestFrom(
        this.store$.select(selectConsultation),
        this.store$.select(selectSelectedProducts),
        this.store$.select(selectAllowance),
        this.store$.select(selectAllowanceCurrentProgram),
        this.store$.select(selectCurrentRecommendation),
        this.store$.select(selectSelectedPackIds),
        this.store$.select(selectCurrentBigMeasurementUnit),
        this.store$.select(selectCurrentClinicCountry),
        this.store$.select(selectTreatsQuantityPerDay),
      ),
      switchMap(
        ([
          { manualWetRation, manualTreatsRation, allocation, ignoreCurrentAllocation },
          { lastConsultation, currentConsultationVisitDate },
          products,
          { allowanceParams: params },
          currentProgram,
          currentRecommendation,
          selectedPackIds,
          currentBigMeasurementUnit,
          country,
          allTreatsQuantityPerDay,
        ]) => {
          if (!params?.deprecatedPetInfo) {
            // Cannot create a recommendation without a pet profile
            return of();
          }

          const petInfo = params?.deprecatedPetInfo;
          manualWetRation = manualWetRation || currentRecommendation?.basicWetRation?.quantityPerDay;
          /** Converting the treats quantity selected to percentage.
           * From the initial rationing call for treats we receive a value for quantity per day which represents 10% of the whole daily rationing.
           * For Giant dogs we offer the user to choose between 5% and 10%, depending on the quantity per day selected by the user we are setting the value accordingly.
           */
          const baseTreatsQuantity = manualTreatsRation ?? currentRecommendation?.basicTreatsRation?.quantityPerDay;
          const treatsProduct = products.find((product) => product.productType === ProductType.Treats);
          manualTreatsRation =
            treatsProduct && baseTreatsQuantity != null ? (baseTreatsQuantity === allTreatsQuantityPerDay ? 10 : 5) : undefined;

          const body = buildRationingApiBody({
            currentProgram,
            products,
            selectedPackIds,
            currentRecommendation,
            currentConsultationVisitDate,
            lastConsultation,
            petInfo,
            manualWetRation,
            manualTreatsRation,
            allocation,
            followUpAction: params?.followUpAction,
            currentBigMeasurementUnit,
            ignoreCurrentAllocation,
          });

          const productIds = [];
          const eans = [];
          const mainItemIds = [];

          for (const { id, ean, mainItemId } of products) {
            if (id) productIds.push(id);
            if (ean) eans.push(ean);
            if (mainItemId) mainItemIds.push(mainItemId);
          }

          return this.nutritionService
            .createRecommendation(currentProgram, body, productIds, eans, mainItemIds, Boolean(body?.dryProduct?.density), country)
            .pipe(
              switchMap((rationRecommendation) => {
                const isGestationPet = petInfo.reproductionStatus === ReproductionStatusCode.Gestation && petInfo.pregnancyWeeks > 0;
                const recommendation =
                  isGestationPet && currentProgram !== Programs.WEIGHT_LOSS
                    ? rationRecommendation[petInfo.pregnancyWeeks - 1]
                    : rationRecommendation[0];

                return [
                  getAllowanceRecommendationSuccess({ recommendation }),
                  ...(manualWetRation || manualTreatsRation || allocation ? [] : [setAllowanceDefaultRecommendation({ recommendation })]),
                  ...(isGestationPet ? [setAllowanceRecommendationForGestation({ recommendation: rationRecommendation })] : []),
                ];
              }),
              catchError((error: string) => of(getAllowanceRecommendationFail({ error }))),
            );
        },
      ),
    ),
  );
  /**
   * Get recommendation fail
   */
  getAllowanceRecommendationFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getAllowanceRecommendationFail),
        withLatestFrom(this.store$.select(selectNavigationRoutes)),
        tap(([action, { editPatientRoute }]) => {
          let errorMessage = $localize`:@@calculator_error_no-ration-provided:`;
          let panelSectionOpen = PanelSectionTypeEnum.PetDetails;
          if (action.error === 'DAC_adult-target-weight-outside-range_no-ration-provided') {
            errorMessage = $localize`:@@DAC_adult-target-weight-outside-range_no-ration-provided:`;
            panelSectionOpen = PanelSectionTypeEnum.PetWeight;
            // display mode edit to allocation bloc
            this.store$.dispatch(setAllowanceCurrentBlockEditing({ block: AllowanceCalculatorBlocks.ALLOCATION, value: true }));
          }
          this.coreFacade.setToast({
            id: Helper.randomId(),
            duration: 30000,
            title: '',
            description: errorMessage,
            type: RcToastTypes.Error,
            dataQa: 'vs-allowance-recommendation-page-toast-error',
            actions: [
              {
                label: translateKey('action_return'),
                actionName: editPatientRoute,
                link: this.router.serializeUrl(
                  this.router.createUrlTree([...editPatientRoute.split('/')], {
                    queryParams: { panelSectionType: panelSectionOpen },
                  }),
                ),
              },
            ],
          });
        }),
      ),
    { dispatch: false },
  );

  /**
   * Get recommendation
   */
  getAllowanceRecommendationAllWetQuantity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAllowanceRecommendationAllWetQuantity),
      withLatestFrom(
        this.store$.select(selectConsultation),
        this.store$.select(selectSelectedWetProduct),
        this.store$.select(selectAllowance),
        this.store$.select(selectAllowanceCurrentProgram),
        this.store$.select(selectCurrentRecommendation),
        this.store$.select(selectSelectedPackIds),
        this.store$.select(selectCurrentBigMeasurementUnit),
        this.store$.select(selectCurrentClinicCountry),
      ),
      switchMap(
        ([
          { allocation, ignoreCurrentAllocation },
          { lastConsultation, currentConsultationVisitDate },
          wetProduct,
          { allowanceParams: params },
          currentProgram,
          currentRecommendation,
          selectedPackIds,
          currentBigMeasurementUnit,
          country,
        ]) => {
          if (!params?.deprecatedPetInfo) {
            // Cannot create a recommendation without a pet profile
            return of();
          }

          const petInfo = params?.deprecatedPetInfo;
          const body = buildRationingApiBody({
            currentProgram,
            products: [wetProduct],
            selectedPackIds,
            currentRecommendation,
            currentConsultationVisitDate,
            lastConsultation,
            petInfo,
            manualWetRation: undefined,
            manualTreatsRation: undefined,
            allocation,
            followUpAction: params?.followUpAction,
            currentBigMeasurementUnit,
            ignoreCurrentAllocation,
          });

          return this.nutritionService
            .createRecommendation(
              currentProgram,
              body,
              wetProduct.id ? [wetProduct.id] : [],
              wetProduct.ean ? [wetProduct.ean] : [],
              wetProduct.mainItemId ? [wetProduct.mainItemId] : [],
              false,
              country,
              false,
            )
            .pipe(
              switchMap((rationRecommendation) => {
                const recommendation =
                  petInfo.reproductionStatus === ReproductionStatusCode.Gestation && petInfo.pregnancyWeeks > 0
                    ? rationRecommendation[petInfo.pregnancyWeeks - 1]
                    : rationRecommendation[0];
                const allWetQuantityPerDay = recommendation.basicWetRation?.quantityPerDay;
                return [getAllWetRecommendationQuantitySuccess({ allWetQuantityPerDay })];
              }),
              catchError((error: string) => of(getAllowanceRecommendationFail({ error }))),
            );
        },
      ),
    ),
  );

  getAllowanceRecommendationAllTreatsQuantity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAllowanceRecommendationAllTreatsQuantity),
      withLatestFrom(
        this.store$.select(selectConsultation),
        this.store$.select(selectSelectedProducts),
        this.store$.select(selectAllowance),
        this.store$.select(selectAllowanceCurrentProgram),
        this.store$.select(selectCurrentRecommendation),
        this.store$.select(selectSelectedPackIds),
        this.store$.select(selectCurrentBigMeasurementUnit),
        this.store$.select(selectCurrentClinicCountry),
      ),
      switchMap(
        ([
          { allocation, ignoreCurrentAllocation },
          { lastConsultation, currentConsultationVisitDate },
          products,
          { allowanceParams: params },
          currentProgram,
          currentRecommendation,
          selectedPackIds,
          currentBigMeasurementUnit,
          country,
        ]) => {
          if (!params?.deprecatedPetInfo) {
            // Cannot create a recommendation without a pet profile
            return of();
          }

          const petInfo = params?.deprecatedPetInfo;
          const body = buildRationingApiBody({
            currentProgram,
            products,
            selectedPackIds,
            currentRecommendation,
            currentConsultationVisitDate,
            lastConsultation,
            petInfo,
            manualWetRation: undefined,
            manualTreatsRation: undefined,
            allocation,
            followUpAction: params?.followUpAction,
            currentBigMeasurementUnit,
            ignoreCurrentAllocation,
          });

          const treatProducts = [products.find((product) => product.productType === ProductType.Treats)].filter((p) => !!p);

          const treatProductIds = [];
          const treatProductEans = [];
          const treatProductMainItemIds = [];

          for (const { id, ean, mainItemId } of treatProducts) {
            if (id) treatProductIds.push(id);
            if (ean) treatProductEans.push(ean);
            if (mainItemId) treatProductMainItemIds.push(mainItemId);
          }

          return this.nutritionService
            .createRecommendation(currentProgram, body, treatProductIds, treatProductEans, treatProductMainItemIds, false, country, false)
            .pipe(
              switchMap((rationRecommendation) => {
                const recommendation =
                  petInfo.reproductionStatus === ReproductionStatusCode.Gestation && petInfo.pregnancyWeeks > 0
                    ? rationRecommendation[petInfo.pregnancyWeeks - 1]
                    : rationRecommendation[0];
                const allTreatsQuantityPerDay = recommendation.basicTreatsRation?.quantityPerDay;
                return [getAllTreatsRecommendationQuantitySuccess({ allTreatsQuantityPerDay })];
              }),
              catchError((error: string) => of(getAllowanceRecommendationFail({ error }))),
            );
        },
      ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<AppState>,
    private readonly nutritionService: NutritionService,
    private readonly router: Router,
    private readonly coreFacade: CoreFacade,
  ) {}
}
